import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";

// Animated sand particle
function SandParticle({ delay }) {
  return (
    <motion.div
      className="absolute w-1 h-1 rounded-full bg-amber-300/60"
      initial={{ x: Math.random() * 100 + "%", y: "-10%", opacity: 0 }}
      animate={{
        x: [
          Math.random() * 100 + "%",
          Math.random() * 100 + "%",
          Math.random() * 100 + "%",
        ],
        y: ["0%", "50%", "110%"],
        opacity: [0, 0.8, 0],
      }}
      transition={{
        duration: Math.random() * 4 + 4,
        delay,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );
}

// Animated star in sky
function Star({ top, left, delay }) {
  return (
    <motion.div
      className="absolute w-1 h-1 rounded-full bg-white"
      style={{ top, left }}
      animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
      transition={{ duration: 2 + Math.random() * 2, delay, repeat: Infinity }}
    />
  );
}

export default function NotFoundPage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handler = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  const stars = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    top: Math.random() * 40 + "%",
    left: Math.random() * 100 + "%",
    delay: Math.random() * 3,
  }));

  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
  }));

  return (
    <>
      <SEO
        title="404 - Page Not Found"
        description="Oops! This page doesn't exist. Let us guide you back to explore Morocco's beautiful desert tours."
      />
      <div className="relative min-h-screen overflow-hidden select-none">
        {/* Sky gradient - night desert */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-amber-950" />

        {/* Stars */}
        {stars.map((s) => (
          <Star key={s.id} top={s.top} left={s.left} delay={s.delay} />
        ))}

        {/* Moon with parallax */}
        <motion.div
          className="absolute top-12 right-16 md:top-16 md:right-32"
          style={{ x: mousePos.x * 0.5, y: mousePos.y * 0.5 }}
        >
          <div className="relative w-20 h-20 md:w-28 md:h-28">
            <div className="absolute inset-0 rounded-full bg-amber-100 shadow-[0_0_60px_20px_rgba(251,191,36,0.3)]" />
            <div className="absolute top-2 right-1 w-6 h-6 md:w-8 md:h-8 rounded-full bg-amber-200/40" />
            <div className="absolute bottom-4 left-3 w-4 h-4 md:w-5 md:h-5 rounded-full bg-amber-200/30" />
          </div>
        </motion.div>

        {/* Sand dunes with parallax */}
        <motion.div
          className="absolute bottom-0 left-0 right-0"
          style={{ x: mousePos.x * -0.3 }}
        >
          {/* Far dune */}
          <svg
            viewBox="0 0 1440 320"
            className="absolute bottom-32 w-[120%] -left-[10%]"
            preserveAspectRatio="none"
          >
            <path
              d="M0,224 C240,160,480,280,720,200 C960,120,1200,260,1440,192 L1440,320 L0,320Z"
              fill="#92400e"
              fillOpacity="0.4"
            />
          </svg>
          {/* Mid dune */}
          <svg
            viewBox="0 0 1440 320"
            className="absolute bottom-16 w-[120%] -left-[10%]"
            preserveAspectRatio="none"
          >
            <path
              d="M0,256 C180,200,360,280,540,240 C720,200,900,260,1080,220 C1260,180,1350,240,1440,224 L1440,320 L0,320Z"
              fill="#b45309"
              fillOpacity="0.5"
            />
          </svg>
          {/* Front dune */}
          <svg
            viewBox="0 0 1440 200"
            className="absolute bottom-0 w-[120%] -left-[10%]"
            preserveAspectRatio="none"
          >
            <path
              d="M0,120 C200,80,400,140,600,100 C800,60,1000,130,1200,90 C1300,70,1380,110,1440,100 L1440,200 L0,200Z"
              fill="#d97706"
              fillOpacity="0.6"
            />
          </svg>
        </motion.div>

        {/* Camel silhouette walking */}
        <motion.div
          className="absolute bottom-20 md:bottom-24"
          animate={{ x: ["-10%", "110%"] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        >
          <svg
            width="100"
            height="60"
            viewBox="0 0 100 60"
            className="opacity-30 w-16 h-10 md:w-24 md:h-14"
          >
            {/* Camel body */}
            <ellipse cx="50" cy="28" rx="22" ry="12" fill="#1c1917" />
            {/* Hump */}
            <ellipse cx="45" cy="18" rx="8" ry="8" fill="#1c1917" />
            {/* Head */}
            <ellipse cx="72" cy="14" rx="6" ry="5" fill="#1c1917" />
            {/* Neck */}
            <path d="M65,22 Q70,15 72,14" stroke="#1c1917" strokeWidth="5" fill="none" />
            {/* Legs */}
            <line x1="35" y1="38" x2="32" y2="55" stroke="#1c1917" strokeWidth="3" />
            <line x1="42" y1="38" x2="40" y2="55" stroke="#1c1917" strokeWidth="3" />
            <line x1="58" y1="38" x2="56" y2="55" stroke="#1c1917" strokeWidth="3" />
            <line x1="64" y1="38" x2="62" y2="55" stroke="#1c1917" strokeWidth="3" />
            {/* Tail */}
            <path d="M28,26 Q22,20 20,24" stroke="#1c1917" strokeWidth="2" fill="none" />
          </svg>
        </motion.div>

        {/* Sand particles */}
        {particles.map((p) => (
          <SandParticle key={p.id} delay={p.delay} />
        ))}

        {/* Main content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
          {/* 404 number */}
          <motion.div
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 12 }}
          >
            <h1 className="text-[8rem] md:text-[12rem] font-black leading-none tracking-tighter">
              <span className="bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent drop-shadow-2xl">
                4
              </span>
              <motion.span
                className="inline-block bg-gradient-to-b from-amber-300 via-orange-400 to-red-500 bg-clip-text text-transparent"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                0
              </motion.span>
              <span className="bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent drop-shadow-2xl">
                4
              </span>
            </h1>
          </motion.div>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="space-y-4 mt-2"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-amber-100">
              Lost in the Sahara?
            </h2>
            <p className="text-amber-200/70 text-base md:text-lg max-w-md mx-auto">
              It seems you've wandered off the trail. Don't worry — even the best
              explorers get lost sometimes. Let us guide you back.
            </p>
          </motion.div>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link
              to="/"
              className="group relative px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2 justify-center">
                <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
                </svg>
                Back Home
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
            <Link
              to="/tours"
              className="group px-8 py-3.5 border-2 border-amber-400/50 text-amber-200 font-semibold rounded-full hover:bg-amber-400/10 hover:border-amber-400 transition-all duration-300 hover:scale-105 flex items-center gap-2 justify-center"
            >
              <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Explore Tours
            </Link>
          </motion.div>

          {/* Compass animation */}
          <motion.div
            className="mt-12 opacity-40"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="text-amber-300">
              <circle cx="12" cy="12" r="10" />
              <polygon points="12,2 15,10 12,8 9,10" fill="currentColor" opacity="0.6" />
              <polygon points="12,22 9,14 12,16 15,14" fill="currentColor" opacity="0.3" />
              <polygon points="2,12 10,9 8,12 10,15" fill="currentColor" opacity="0.3" />
              <polygon points="22,12 14,15 16,12 14,9" fill="currentColor" opacity="0.3" />
            </svg>
          </motion.div>
        </div>
      </div>
    </>
  );
}
